import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, SlidersHorizontal } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../components/ui/pagination";
import Seo from "../lib/Seo";
import CenterCard from "../components/CenterCard";
import Breadcrumbs from "../components/Breadcrumbs";
import {
    fetchCenters,
    CHILE_REGIONS,
    MODALITIES,
    GENDERS,
} from "../lib/api";

const LIMIT = 12;

export default function Centros() {
    const [params, setParams] = useSearchParams();
    const [centers, setCenters] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const page = parseInt(params.get("page") || "1", 10);
    const region = params.get("region") || "";
    const modality = params.get("modality") || "";
    const gender = params.get("gender") || "";
    const city = params.get("city") || "";
    const search = params.get("search") || "";
    const verifiedOnly = params.get("is_verified") === "true";

    const [searchInput, setSearchInput] = useState(search);
    const [cityInput, setCityInput] = useState(city);

    const queryParams = useMemo(() => {
        const q = { page, limit: LIMIT };
        if (region) q.region = region;
        if (modality) q.modality = modality;
        if (gender) q.gender = gender;
        if (city) q.city = city;
        if (search) q.search = search;
        if (verifiedOnly) q.is_verified = true;
        return q;
    }, [page, region, modality, gender, city, search, verifiedOnly]);

    useEffect(() => {
        setLoading(true);
        setError(false);
        const controller = new AbortController();
        let cancelled = false;

        async function load() {
            // Simple retry on transient upstream failures (proxy 502 etc.)
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    const data = await fetchCenters(queryParams, { signal: controller.signal });
                    if (cancelled) return;
                    setCenters(data.centers || []);
                    setTotal(data.total || 0);
                    setPages(data.pages || 1);
                    setError(false);
                    return;
                } catch (err) {
                    if (cancelled || err.name === "CanceledError" || err.name === "AbortError") return;
                    if (attempt === 0) {
                        await new Promise((r) => setTimeout(r, 600));
                        continue;
                    }
                    setCenters([]);
                    setTotal(0);
                    setPages(1);
                    setError(true);
                }
            }
        }

        load().finally(() => {
            if (!cancelled) setLoading(false);
        });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [queryParams]);

    function update(patch) {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([k, v]) => {
            if (v === "" || v === null || v === undefined || v === false) next.delete(k);
            else next.set(k, v);
        });
        if (!("page" in patch)) next.set("page", "1");
        setParams(next, { replace: false });
    }

    function applySearch(e) {
        e.preventDefault();
        update({ search: searchInput.trim(), city: cityInput.trim() });
    }

    function clearAll() {
        setSearchInput("");
        setCityInput("");
        setParams(new URLSearchParams());
    }

    const Filters = (
        <div className="space-y-6">
            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">
                    Región
                </Label>
                <Select
                    value={region || "all"}
                    onValueChange={(v) => update({ region: v === "all" ? "" : v })}
                >
                    <SelectTrigger
                        data-testid="filter-region"
                        className="mt-2 border-slate-800 bg-slate-950 text-slate-100"
                    >
                        <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent className="border-slate-800 bg-slate-950 text-slate-100">
                        <SelectItem value="all">Todas</SelectItem>
                        {CHILE_REGIONS.map((r) => (
                            <SelectItem key={r} value={r}>
                                {r}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">
                    Modalidad
                </Label>
                <Select
                    value={modality || "all"}
                    onValueChange={(v) => update({ modality: v === "all" ? "" : v })}
                >
                    <SelectTrigger
                        data-testid="filter-modality"
                        className="mt-2 border-slate-800 bg-slate-950 text-slate-100"
                    >
                        <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent className="border-slate-800 bg-slate-950 text-slate-100">
                        <SelectItem value="all">Todas</SelectItem>
                        {MODALITIES.map((m) => (
                            <SelectItem key={m} value={m}>
                                {m}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">
                    Género
                </Label>
                <Select
                    value={gender || "all"}
                    onValueChange={(v) => update({ gender: v === "all" ? "" : v })}
                >
                    <SelectTrigger
                        data-testid="filter-gender"
                        className="mt-2 border-slate-800 bg-slate-950 text-slate-100"
                    >
                        <SelectValue placeholder="Cualquiera" />
                    </SelectTrigger>
                    <SelectContent className="border-slate-800 bg-slate-950 text-slate-100">
                        <SelectItem value="all">Cualquiera</SelectItem>
                        {GENDERS.map((g) => (
                            <SelectItem key={g.value} value={g.value}>
                                {g.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">
                    Ciudad
                </Label>
                <Input
                    data-testid="filter-city"
                    value={cityInput}
                    onChange={(e) => setCityInput(e.target.value)}
                    onBlur={() => update({ city: cityInput.trim() })}
                    placeholder="Ej: Viña del Mar"
                    className="mt-2 border-slate-800 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                />
            </div>

            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-800 bg-slate-950 p-3 text-sm text-slate-200">
                <Checkbox
                    checked={verifiedOnly}
                    onCheckedChange={(v) => update({ is_verified: v ? "true" : "" })}
                    data-testid="filter-verified"
                />
                Solo centros verificados
            </label>

            <Button
                variant="outline"
                onClick={clearAll}
                data-testid="filter-clear"
                className="w-full border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-slate-50"
            >
                Limpiar filtros
            </Button>
        </div>
    );

    return (
        <>
            <Seo
                title="Directorio de centros de rehabilitación de drogas en Chile"
                description="Explora más de 88 centros de rehabilitación de drogas en Chile. Filtra por región, modalidad, género y precio. Información clínica verificada."
                path="/centros"
                keywords="directorio centros de rehabilitación, centros de rehabilitación Chile, internado drogadictos, rehabilitación adicciones"
                noindex={Boolean(region || modality || gender || city || search || verifiedOnly || page > 1)}
            />
            <div className="border-b border-slate-800 bg-slate-950">
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    <Breadcrumbs
                        items={[
                            { name: "Inicio", path: "/" },
                            { name: "Centros" },
                        ]}
                    />
                    <h1 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                        Directorio de centros de rehabilitación de drogas
                    </h1>
                    <p className="mt-2 max-w-2xl text-slate-400">
                        Explora {total > 0 ? total : "88+"} centros verificados en las 16
                        regiones de Chile. Filtra por modalidad, presupuesto y género.
                    </p>
                    <form
                        onSubmit={applySearch}
                        className="mt-6 flex max-w-2xl gap-2"
                        data-testid="centros-search-form"
                    >
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                            <Input
                                data-testid="centros-search-input"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Buscar por nombre, sustancia, servicio..."
                                className="border-slate-800 bg-slate-900 pl-9 text-slate-100 placeholder:text-slate-500"
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="centros-search-submit"
                            className="bg-cyan-500 text-slate-950 hover:bg-cyan-400"
                        >
                            Buscar
                        </Button>
                    </form>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
                    <aside className="hidden lg:block">
                        <div className="sticky top-24 rounded-xl border border-slate-800 bg-slate-900 p-5">
                            <div className="mb-4 flex items-center gap-2 text-slate-100">
                                <SlidersHorizontal className="h-4 w-4 text-cyan-400" />
                                <h2 className="text-sm font-semibold">Filtros</h2>
                            </div>
                            {Filters}
                        </div>
                    </aside>

                    <div>
                        <div className="mb-4 flex items-center justify-between lg:hidden">
                            <Button
                                variant="outline"
                                onClick={() => setMobileFiltersOpen((v) => !v)}
                                data-testid="centros-mobile-filters-toggle"
                                className="border-slate-700 bg-slate-900 text-slate-100"
                            >
                                <Filter className="mr-2 h-4 w-4" />
                                Filtros
                            </Button>
                            <span className="text-sm text-slate-400">
                                {loading ? "Cargando..." : `${total} resultados`}
                            </span>
                        </div>
                        {mobileFiltersOpen ? (
                            <div className="mb-4 rounded-xl border border-slate-800 bg-slate-900 p-5 lg:hidden">
                                {Filters}
                            </div>
                        ) : null}

                        <div className="mb-4 hidden items-center justify-between lg:flex">
                            <span className="text-sm text-slate-400" data-testid="centros-total">
                                {loading
                                    ? "Cargando centros..."
                                    : `${total} resultado${total === 1 ? "" : "s"}`}
                            </span>
                            <span className="text-xs text-slate-500">
                                {loading ? "" : `Página ${page} de ${pages}`}
                            </span>
                        </div>

                        {loading ? (
                            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="h-80 animate-pulse rounded-xl border border-slate-800 bg-slate-900"
                                    />
                                ))}
                            </div>
                        ) : error ? (
                            <div
                                data-testid="centros-error"
                                className="rounded-xl border border-dashed border-slate-800 bg-slate-900 p-10 text-center"
                            >
                                <p className="text-slate-300">
                                    No pudimos cargar los centros en este momento.
                                </p>
                                <Button
                                    onClick={() => setParams(new URLSearchParams(params))}
                                    className="mt-4 bg-cyan-500 text-slate-950 hover:bg-cyan-400"
                                >
                                    Reintentar
                                </Button>
                            </div>
                        ) : centers.length === 0 ? (
                            <div
                                data-testid="centros-empty"
                                className="rounded-xl border border-dashed border-slate-800 bg-slate-900 p-10 text-center"
                            >
                                <p className="text-slate-300">
                                    No encontramos centros con esos filtros.
                                </p>
                                <Button
                                    onClick={clearAll}
                                    className="mt-4 bg-cyan-500 text-slate-950 hover:bg-cyan-400"
                                >
                                    Limpiar filtros
                                </Button>
                            </div>
                        ) : (
                            <div
                                className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
                                data-testid="centros-grid"
                            >
                                {centers.map((c) => (
                                    <CenterCard key={c.center_id} center={c} />
                                ))}
                            </div>
                        )}

                        {pages > 1 ? (
                            <Pagination className="mt-10" data-testid="centros-pagination">
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (page > 1) update({ page: String(page - 1) });
                                            }}
                                            className={`cursor-pointer text-slate-200 hover:text-cyan-400 ${
                                                page === 1
                                                    ? "pointer-events-none opacity-40"
                                                    : ""
                                            }`}
                                        />
                                    </PaginationItem>
                                    {Array.from({ length: Math.min(5, pages) }).map((_, i) => {
                                        const start = Math.max(1, Math.min(page - 2, pages - 4));
                                        const p = start + i;
                                        if (p > pages) return null;
                                        return (
                                            <PaginationItem key={p}>
                                                <PaginationLink
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        update({ page: String(p) });
                                                    }}
                                                    isActive={p === page}
                                                    className={`cursor-pointer ${
                                                        p === page
                                                            ? "bg-cyan-500 text-slate-950"
                                                            : "text-slate-200 hover:bg-slate-800"
                                                    }`}
                                                >
                                                    {p}
                                                </PaginationLink>
                                            </PaginationItem>
                                        );
                                    })}
                                    <PaginationItem>
                                        <PaginationNext
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (page < pages)
                                                    update({ page: String(page + 1) });
                                            }}
                                            className={`cursor-pointer text-slate-200 hover:text-cyan-400 ${
                                                page === pages
                                                    ? "pointer-events-none opacity-40"
                                                    : ""
                                            }`}
                                        />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
}
